import { useContext } from 'react';
import { AuthProvider, useAuth } from 'react-oidc-context';
import { useSearchParams } from 'react-router-dom';

import { InformationPage } from '.';
import { benefitOidcConfig } from '../../../../common/auth/oidcConfig';
import {
  appTypes,
  LEGAL_INFORMATION_IDS,
  LegalInformationType,
} from '../../../../common/constants';
import { config } from '../../../../common/global/config';
import PublicLayout from '../../../../common/global/public-layout';
import {
  CustomerInfoContext,
  CustomerInfoProvider,
} from '../../contexts/CustomerInfoContext';
import { PublicInformationLoader } from './PublicInformationLoader';

interface PublicInformationRouteProps {
  type: LegalInformationType;
}

const DefaultInformationPage = ({ type }: PublicInformationRouteProps) => (
  <PublicLayout>
    <InformationPage informationId={LEGAL_INFORMATION_IDS[type].default} />
  </PublicLayout>
);

const AuthenticatedPublicInformationPage = ({
  type,
}: PublicInformationRouteProps) => {
  const { customerInfo, isLoading } = useContext(CustomerInfoContext);

  if (isLoading) {
    return <PublicInformationLoader />;
  }

  const ids = LEGAL_INFORMATION_IDS[type];
  const informationId = customerInfo.isSavingsUser ? ids.savings : ids.default;

  return (
    <PublicLayout>
      <InformationPage informationId={informationId} />
    </PublicLayout>
  );
};

const PublicInformationContent = ({ type }: PublicInformationRouteProps) => {
  const auth = useAuth();

  if (auth.isLoading) {
    return <PublicInformationLoader />;
  }

  if (!auth.isAuthenticated) {
    return <DefaultInformationPage type={type} />;
  }

  return (
    <CustomerInfoProvider>
      <AuthenticatedPublicInformationPage type={type} />
    </CustomerInfoProvider>
  );
};

export const PublicInformationRoute = ({
  type,
}: PublicInformationRouteProps) => {
  const [searchParams] = useSearchParams();

  const savingsParam = searchParams.get('savings');

  let isSavingsFromQuery: boolean | undefined;

  if (savingsParam === 'true') {
    isSavingsFromQuery = true;
  } else if (savingsParam === 'false') {
    isSavingsFromQuery = false;
  }

  if (isSavingsFromQuery !== undefined) {
    const ids = LEGAL_INFORMATION_IDS[type];
    const informationId = isSavingsFromQuery ? ids.savings : ids.default;

    return (
      <PublicLayout>
        <InformationPage informationId={informationId} />
      </PublicLayout>
    );
  }

  if (config.appType !== appTypes.benefit) {
    return <DefaultInformationPage type={type} />;
  }

  return (
    <AuthProvider {...benefitOidcConfig}>
      <PublicInformationContent type={type} />
    </AuthProvider>
  );
};
